import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import Modal, { ModalAlert } from '@/components/modal/modal.component'
import TextAreaWordCount from '@/components/SHARED/text-area-word-count/text-area-word-count.component'
import { UseCloudFunction } from '@/utils/firebase.utils'
import useAnalytics from '@/utils/useAnalytics'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

// message is the news item being edited, or null to post a new one.
const MessageModal = ({ gameData, show, cancel, message }) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const membership = useSelector(state => state.userData.membership);
    const [ formData, setFormData ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    // TextAreaWordCount ignores an empty existingText, so it is remounted on
    // every open rather than carrying the last item's text into a new one.
    const [ formKey, setFormKey ] = useState(0);
    const { trackEvent, trackException } = useAnalytics();
    const dataRef = useRef({});
    const location = useLocation();

    useEffect(() => {
        if (!show) return;
        dataRef.current = message ? {...message} : {};
        setFormData({...dataRef.current});
        setFormKey(k => k + 1);
    }, [show, message])

    function saveData(key, value) {
        dataRef.current = {...dataRef.current, ...{[key]: value}}
        setFormData({...dataRef.current});
    }

    async function saveMessage() {
        if (location.pathname.includes('/preview/manager/')) {
            setAlertMessage('Sorry! You cannot post messages in Preview Mode.');
            return;
        }
        if (!dataRef.current.message?.trim()) {
            setAlertMessage('Please add some content to your news item.');
            return;
        }
        setLoading(true);
        let tempData = {...dataRef.current};
        const now = new Date().getTime();
        const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-')-5, gameData.gameId.indexOf('-')+6)
        const isNew = !tempData.id;
        if (isNew) {
            tempData.id = now + '_' + gameCode + '_message'
            tempData.ts_created = now;
            tempData.gameId = gameData.gameId;
            tempData.appId = userProfile.appId;
            tempData.active = true;
            tempData.ts_activated = now;
        }
        tempData.ts = now;

        const res = await UseCloudFunction(
            'saveMessage',
            {
                'gameId': gameData.gameId,
                'data': tempData
            }
        )
        setLoading(false);
        if (res.error) {
            setAlertMessage(`Something went wrong. Error: ${res.error}`);
            trackException(`Message Post Error: ${res.error}`);
            return;
        }
        setAlertMessage(isNew ? 'News item posted successfully!' : 'News item updated successfully!');
        trackEvent(
            isNew ? 'message_posted' : 'message_edited',
            'Game Manager',
            `Message ${isNew ? 'posted' : 'edited'} for game ${gameData.gameId}.`,
            null,
            membership
        );
        cancel();
    }

    return (
        <div className='message-modal'>
            <Modal
                show={show}
                cancel={cancel}
                closeButton={true}
                text={message ? 'Edit News Item' : "Post News to Your Players"}
                cls="narrow"
            >
                <div className='meta'>
                    {
                        message
                        ? 'Saving changes does not make the popup reappear for players who already closed it. Their next popup will show the updated version.'
                        : 'New items start out active, so they pop up on players\' home pages. You can switch an item off from the Game News list at any time.'
                    }
                </div>
                <div className="g-space-1"></div>
                <div className='field'>
                    <label>Title</label>
                    <input
                        type='text'
                        value={formData.title ?? ''}
                        onChange={(e) => saveData('title', e.target.value)}
                    />
                </div>
                <div className="g-space-1"></div>
                <div className='field'>
                    <label>Content</label>
                    <TextAreaWordCount
                        key={formKey}
                        count={150}
                        saveData={saveData}
                        existingText={formData.message ?? ''}
                        keyId='message'
                    />
                </div>
                <div className="g-space-1"></div>
                {
                    (loading)
                    ?
                    <div className='buttons'>
                        <button className='g-button'>Cancel</button>
                        <button className='g-button'><ThreeDotsLoader /></button>
                    </div>
                    :
                    <div className='buttons'>
                        <button
                            className='g-button'
                            onClick={() => cancel()}
                        >
                            Cancel
                        </button>
                        <button
                            className='g-button primary'
                            onClick={() => saveMessage()}
                        >
                            {message ? 'Save Changes' : 'Post News Item'}
                        </button>
                    </div>
                }
            </Modal>
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage}
            />
        </div>
    )
}

export default MessageModal
